import { useRef, useState } from "react";

import { type AnyFieldProps, type AnyFieldRef, type Validity, VALIDITY_OK } from "./utilities";

type FieldValue<P> = P extends AnyFieldProps<infer V> ? V : never;

type UseFieldInput<P extends AnyFieldProps<FieldValue<P>>> = Omit<
    P,
    "onChange" | "onValidityChange"
>;

type UseFieldOutput<P extends AnyFieldProps<FieldValue<P>>, Required extends boolean> = {
    props: P;
    setValue: (value: FieldValue<P>) => void;
    validate: () => Required extends true ? FieldValue<P> : FieldValue<P> | null;
};

export default function useField<P extends AnyFieldProps<FieldValue<P>>>(
    props: UseFieldInput<P> & { required: true },
): UseFieldOutput<P, true>;
export default function useField<P extends AnyFieldProps<FieldValue<P>>>(
    props: UseFieldInput<P>,
): UseFieldOutput<P, false>;
export default function useField<P extends AnyFieldProps<FieldValue<P>>>(
    props: UseFieldInput<P>,
): UseFieldOutput<P, boolean> {
    const [value, setValue] = useState<FieldValue<P> | null | undefined>(props.value);
    const validityRef = useRef<Validity>(VALIDITY_OK);
    const handle = useRef<AnyFieldRef<FieldValue<P>>>(null);
    return {
        props: {
            ...props,
            value,
            onChange: setValue,
            onValidityChange: (validity) => (validityRef.current = validity),
            ref: handle,
        } as P,
        setValue,
        validate: () => {
            if (!handle.current) throw new Error("Field not initialized");
            return handle.current.validate();
        },
    };
}
